import React, { useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import CustomDialog from '../../../components/common/CustomDialog';
import { getAllExcelData } from "../../../utils/urls";
import { errorToast, successToast } from "../../../utils/toasts";
const DownloadExcel = () => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Open the confirmation dialog when the Download Excel button is clicked
  const handleDownloadClick = () => {
    setIsConfirmDialogOpen(true);
  };

  // Handle confirmation to proceed with download
  const handleConfirmDownload = () => {
    setIsConfirmDialogOpen(false);
    setIsPopupOpen(true);
    setCurrentStep(1);
    setProgress(0);
    setAllData([]);
    setError(null);
    fetchData();
  };

  // Handle try again after error
  const handleTryAgain = () => {
    setError(null);
    setCurrentStep(1);
    setProgress(0);
    setAllData([]);
    fetchData();
  };

  // Fetch data with pagination using limit and page_number
  const fetchData = async () => {
    setIsLoading(true);
    let fetchedData = [];
    let pageNumber = 1;
    const limit = 50;
    let hasMoreData = true;

    try {
      while (hasMoreData) {
        const response = await axios.get(`${getAllExcelData}?limit=${limit}&page_number=${pageNumber}`);
        
        const data = response.data.data || response.data;
        fetchedData = [...fetchedData, ...data];
        console.log("data",data);
        
        // Update progress (estimate based on fetches)
        setProgress(Math.min((fetchedData.length / (fetchedData.length + 50)) * 100, 90));

        // Check if there's more data to fetch
        if (data.length < limit) {
          hasMoreData = false;
        } else {
          pageNumber += 1;
        }
      }

      setAllData(fetchedData);
      setProgress(100); // Step 1 complete
      setCurrentStep(2); // Move to Step 2
      console.log("fetchedData",fetchedData);
      
      generateExcel(fetchedData);
      
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again.');
      setIsLoading(false);
    }
  };

  // Generate and download Excel
  const generateExcel = async (data) => {
    setProgress(0); // Reset progress for Step 2

    try {
      // Simulate progress for Excel generation
      for (let i = 0; i <= 100; i += 20) {
        setProgress(i);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Create a new workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'exported_data.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Close the popup after download
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsLoading(false);
        successToast("Downloaded Excel file successfully");
      }, 500);
    } catch (error) {
      console.error('Error generating Excel:', error);
      setError('Failed to generate Excel file. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4">
      {/* Download Excel Button */}
      <button
        onClick={handleDownloadClick}
        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
        disabled={isLoading}
      >
        Download Excel
      </button>

      {/* Confirmation Dialog */}
      <CustomDialog
        open={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        title="Are You Sure?"
        titleClassName="text-center"
        className=""
      >
        <div className="w-[400px] text-center">
          <p className="text-gray-600 text-left border-b pb-4">
            Are you sure you want to download the Excel file?
          </p>
          <div className="flex justify-between gap-3 mt-4">
            <button
              className="py-2 px-4 bg-gray-200 font-semibold text-base w-[170px] rounded-md"
              onClick={() => setIsConfirmDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              className="py-2 px-4 bg-blue-500 w-[170px] font-semibold text-base text-white rounded-md disabled:opacity-70"
              onClick={handleConfirmDownload}
              disabled={isLoading}
            >
              Confirm
            </button>
          </div>
        </div>
      </CustomDialog>

      {/* Progress Popup Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 w-[400px] shadow-lg">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                {error
                  ? 'Error Occurred'
                  : currentStep === 1
                  ? 'Fetching Data...'
                  : 'Generating Excel...'}
              </h2>
              <button
                onClick={() => setIsPopupOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error Message or Progress */}
            {error ? (
              <div>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={handleTryAgain}
                  className="w-full py-2 px-4 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-600"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                {/* Step Indicators */}
                <div className="flex justify-between mb-4">
                  <div className={`text-sm ${currentStep === 1 ? 'font-bold text-blue-600' : 'text-gray-500'}`}>
                    Step 1: Fetch Data
                  </div>
                  <div className={`text-sm ${currentStep === 2 ? 'font-bold text-blue-600' : 'text-gray-500'}`}>
                    Step 2: Generate Excel
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                  <div
                    className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="text-center text-sm text-gray-600">
                 {Math.floor(progress)}% Complete
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadExcel;